import { useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Spinner,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaCopy, FaReply } from 'react-icons/fa';

import { toaster } from '#src/components/ui/toaster';
import { CREATE_MESSAGE } from '#src/graphql/mutations/create-message';
import { GET_MESSAGES } from '#src/graphql/queries/messages';
import {
  usePageFooter,
  usePageLayout,
} from '#src/providers/page-layout-provider';
import { useCurrentUser } from '#src/providers/user-provider';

export function Dashboard() {
  const { footerRef } = usePageFooter();
  const { setFooterMode } = usePageLayout();
  const user = useCurrentUser();

  useEffect(() => {
    setFooterMode('fixed');
    return () => setFooterMode('float');
  }, [setFooterMode]);
  const [messageText, setMessageText] = useState('');
  const [parentTargetId, setParentTargetId] = useState<string | null>(null);

  const [createMessage, { loading: isSendingMessage }] =
    useMutation(CREATE_MESSAGE);

  const {
    data: messagesData,
    loading: isLoadingMessages,
    error: messagesError,
    refetch: refetchMessages,
  } = useQuery(GET_MESSAGES, {
    fetchPolicy: 'cache-and-network',
  });

  const filteredMessages = [...(messagesData?.messages || [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [filteredMessages]);

  const handleSendMessage = async () => {
    const value = messageText.trim();
    if (!value) return;
    try {
      await createMessage({
        variables: {
          input: {
            value,
            parentMessageId: parentTargetId ?? undefined,
          },
        },
      });
      setMessageText('');
      await refetchMessages();
      toaster.create({
        title: 'Message sent',
        description: 'Your message was created successfully',
        type: 'success',
      });
    } catch (error) {
      toaster.create({
        title: 'Failed to send message',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    }
  };

  const footerContent = user.email ? (
    <VStack gap={2} align="stretch" width="100%">
      {parentTargetId && (
        <HStack gap={2} align="center" fontSize="xs" color="fg.muted">
          <Text>Replying to message</Text>
          <Button
            size="xs"
            variant="ghost"
            onClick={() => setParentTargetId(null)}
          >
            Cancel
          </Button>
        </HStack>
      )}
      <HStack gap={2} align="flex-end" width="100%">
        <Textarea
          flex={1}
          placeholder="Type a message..."
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          size="sm"
          fontSize="16px"
          rows={2}
          resize="none"
          minH="40px"
          maxH="120px"
        />
        <Button
          size="sm"
          colorScheme="blue"
          onClick={handleSendMessage}
          disabled={!messageText.trim() || isSendingMessage}
          loading={isSendingMessage}
          loadingText="Sending..."
        >
          Send
        </Button>
      </HStack>
    </VStack>
  ) : null;

  return (
    <>
      {/* Render message input into footer portal */}
      {footerRef.current && createPortal(footerContent, footerRef.current)}

      <Box display="flex" flexDirection="column" height="100%" minHeight={0}>
        <HStack
          flex={1}
          minHeight={0}
          align="stretch"
          gap={4}
          overflow="hidden"
        >
          <Box flex={1} minHeight={0} overflowY="auto">
            {!user.email ? (
              <Alert.Root status="info">
                <Alert.Indicator />
                <Alert.Title>
                  Please set your user email in Settings to view messages.
                </Alert.Title>
              </Alert.Root>
            ) : isLoadingMessages ? (
              <Box textAlign="center" py={8}>
                <Spinner size="lg" color="blue.500" />
                <Text fontSize="sm" color="fg.muted" mt={4}>
                  Loading messages...
                </Text>
              </Box>
            ) : messagesError ? (
              <Alert.Root status="error">
                <Alert.Indicator />
                <Alert.Title>
                  Error loading messages: {messagesError.message}
                </Alert.Title>
              </Alert.Root>
            ) : filteredMessages.length === 0 ? (
              <Text color="fg.muted" textAlign="center" py={8}>
                No messages found.
              </Text>
            ) : (
              <VStack gap={3} align="stretch">
                {filteredMessages.map((message) => (
                  <Box key={message.id}>
                    <Box
                      p={4}
                      bg="bg.subtle"
                      borderRadius="md"
                      border="1px"
                      borderColor="border"
                    >
                      <HStack justify="space-between" align="start" mb={2}>
                        <HStack gap={2} align="center">
                          <Text fontSize="xs" fontWeight="medium" color="fg">
                            {message.author?.displayName ?? 'Unknown'}
                          </Text>
                          <Text fontSize="xs" color="fg.muted">
                            ·
                          </Text>
                          <Text fontSize="xs" color="fg.muted">
                            {new Date(message.createdAt).toLocaleString()}
                          </Text>
                        </HStack>
                        <HStack gap={2} align="center">
                          <Text fontSize="xs" color="fg.subtle">
                            {message.source}
                          </Text>
                          {parentTargetId === message.id ? (
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => setParentTargetId(null)}
                            >
                              Cancel reply
                            </Button>
                          ) : (
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => setParentTargetId(message.id)}
                            >
                              <FaReply />
                              Reply to this
                            </Button>
                          )}
                        </HStack>
                      </HStack>
                      <Text fontSize="sm" whiteSpace="pre-wrap">
                        {message.value}
                      </Text>
                      <HStack gap={2} align="center" mt={2} wrap="wrap">
                        <HStack gap={1} align="center">
                          <Text fontSize="xs" color="fg.subtle">
                            ID: {message.id}
                          </Text>
                          <IconButton
                            size="xs"
                            variant="ghost"
                            color="fg.subtle"
                            _hover={{ color: 'fg.muted' }}
                            onClick={() => {
                              navigator.clipboard.writeText(message.id);
                            }}
                          >
                            <FaCopy />
                          </IconButton>
                        </HStack>
                        {message.messageContexts &&
                          message.messageContexts.length > 0 && (
                            <HStack gap={1} align="center" wrap="wrap">
                              {message.messageContexts.map((context, index) => (
                                <Badge
                                  key={`${context.name}-${index}`}
                                  size="sm"
                                  colorScheme="blue"
                                  variant="subtle"
                                  fontSize="xs"
                                  px={2}
                                  py={1}
                                >
                                  {context.name}: {context.value}
                                </Badge>
                              ))}
                            </HStack>
                          )}
                      </HStack>
                    </Box>
                    {message.childMessages &&
                      message.childMessages.length > 0 && (
                        <VStack
                          gap={2}
                          align="stretch"
                          mt={2}
                          pl={6}
                          borderLeftWidth="2px"
                          borderLeftColor="border"
                        >
                          {message.childMessages.map((child) => (
                            <Box
                              key={child.id}
                              p={4}
                              bg="bg.subtle"
                              borderRadius="md"
                              border="1px"
                              borderColor="border"
                            >
                              <HStack
                                justify="space-between"
                                align="start"
                                mb={2}
                              >
                                <HStack gap={2} align="center">
                                  <Text
                                    fontSize="xs"
                                    fontWeight="medium"
                                    color="fg"
                                  >
                                    {child.author?.displayName ?? 'Unknown'}
                                  </Text>
                                  <Text fontSize="xs" color="fg.muted">
                                    ·
                                  </Text>
                                  <Text fontSize="xs" color="fg.muted">
                                    {new Date(child.createdAt).toLocaleString()}
                                  </Text>
                                </HStack>
                                <Text fontSize="xs" color="fg.subtle">
                                  {child.source}
                                </Text>
                              </HStack>
                              <Text fontSize="sm" whiteSpace="pre-wrap">
                                {child.value}
                              </Text>
                              {child.messageContexts &&
                                child.messageContexts.length > 0 && (
                                  <HStack
                                    gap={1}
                                    align="center"
                                    mt={2}
                                    wrap="wrap"
                                  >
                                    {child.messageContexts.map(
                                      (context, index) => (
                                        <Badge
                                          key={`${context.name}-${index}`}
                                          size="sm"
                                          colorScheme="blue"
                                          variant="subtle"
                                          fontSize="xs"
                                          px={2}
                                          py={1}
                                        >
                                          {context.name}: {context.value}
                                        </Badge>
                                      ),
                                    )}
                                  </HStack>
                                )}
                            </Box>
                          ))}
                        </VStack>
                      )}
                  </Box>
                ))}
                <Box ref={messagesEndRef} />
              </VStack>
            )}
          </Box>
        </HStack>
      </Box>
    </>
  );
}
